import { useState } from 'react';

import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { Header } from '@components/header';
import { IconHelp } from '@components/icons/help';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTriggerOff } from '@components/icons/trigger-off';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContainer } from '@components/page-containers';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useIntercom } from '@hooks/use-intercom';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { formatNumber } from '@utils/numbers';

import { useCurrentUser } from 'api/authentication/hooks';
import { PlanCurrencies, PlanFrequencies } from 'api/authentication/requests';
import { useGetChargeBeePlanPage } from 'api/company/hooks';
import { useCancelDowngrade } from 'api/plans/hooks';

import { CurrencySelector } from './currency-selector';
import { getPlanPrice, resolveSelectedCurrency } from './plan-pricing';

const PLAN_HIERARCHY = {
  enterprise: 3,
  premium: 2,
  business: 1,
  trial: 0,
  demo_external: 0,
  demo_internal: 0,
};

const DEMO_PLANS = ['demo_external', 'demo_internal'];
const PAYING_PLANS = ['business', 'premium', 'enterprise'];

export const Plans = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <>
      <Header
        actions={
          <Button
            variant="secondary"
            iconLeft={<IconHelp />}
            text={t('learnMore')}
            href="https://www.50skills.com/pricing"
            target="_blank"
          />
        }
        breadcrumbs={<Breadcrumb items={[{ label: t('plans') }]} />}
      />
      <PageContainer.PageContent>
        <Stack space={6}>
          <PlanPicker />
          <FeatureList />
        </Stack>
      </PageContainer.PageContent>
    </>
  );
};

const PlanPicker = () => {
  const { t } = useScopedTranslate(locale);
  const chargebeePlanPageMutation = useGetChargeBeePlanPage();
  const currentUser = useCurrentUser();
  const { showMessenger } = useIntercom();
  const [isMonthly, setIsMonthly] = useState(true);
  const [planLoading, setPlanLoading] = useState<'business' | 'premium' | null>(null);

  const currentPlan = currentUser?.plan?.planType ?? 'demo_internal';
  const downgradeToPlanType = currentUser?.plan?.downgradeToPlanType ?? null;

  const planCurrency = currentUser?.plan?.planCurrency ?? 'USD';

  const isCurrencyLocked = PAYING_PLANS.includes(currentPlan);
  const [pickedCurrency, setPickedCurrency] = useState<string | null>(null);
  const currency = isCurrencyLocked
    ? resolveSelectedCurrency(planCurrency, true)
    : (pickedCurrency ?? resolveSelectedCurrency(planCurrency, false));
  const frequency: PlanFrequencies = isMonthly ? 'monthly' : 'yearly';
  const businessPrice = getPlanPrice(currency, frequency, 'business');
  const premiumPrice = getPlanPrice(currency, frequency, 'premium');

  const getButtonText = (targetPlan: 'business' | 'premium' | 'enterprise') => {
    const currentValue = PLAN_HIERARCHY[currentPlan];
    const targetValue = PLAN_HIERARCHY[targetPlan];

    if (currentValue === 0) {
      return targetPlan === 'enterprise' ? t('getAQuote') : t('upgrade');
    }
    if (currentValue === targetValue) {
      return t('currentPlan');
    }
    if (targetValue < currentValue) {
      return t('downgrade');
    }
    return targetPlan === 'enterprise' ? t('getAQuote') : t('upgrade');
  };

  const getButtonVariant = (targetPlan: 'business' | 'premium' | 'enterprise') => {
    if (targetPlan === 'enterprise') {
      return 'primary';
    }
    if (
      currentPlan &&
      (currentPlan === targetPlan || PLAN_HIERARCHY[targetPlan] < PLAN_HIERARCHY[currentPlan])
    ) {
      return 'secondary';
    }
    return 'primary-blue';
  };

  const handlePlanButtonClick = async (targetPlan: 'business' | 'premium') => {
    if (DEMO_PLANS.includes(currentPlan)) {
      return;
    }
    if (getPlanPrice(currency, frequency, targetPlan) === null) {
      showMessenger();
      return;
    }
    if (targetPlan === 'business') {
      setPlanLoading('business');
      const planPageData = await chargebeePlanPageMutation.mutateAsync({
        planType: 'business',
        currency: currency as PlanCurrencies,
        frequency: isMonthly ? 'monthly' : 'yearly',
      });
      if (!planPageData) return;
      return window.open(planPageData.url, '_blank');
    } else {
      setPlanLoading('premium');
      const planPageData = await chargebeePlanPageMutation.mutateAsync({
        planType: 'premium',
        currency: currency as PlanCurrencies,
        frequency: isMonthly ? 'monthly' : 'yearly',
      });
      if (!planPageData) return;
      return window.open(planPageData.url, '_blank');
    }
  };

  return (
    <Card space={6} variant="gray" hasBorder={false}>
      <Stack space={4}>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Text font="bold18" text={t('pickAPlan')} />
          <Stack orientation="horizontal" space={4} align="center">
            <Button
              rounded
              variant={isMonthly ? 'primary' : 'secondary'}
              text={t('monthly')}
              onClick={() => setIsMonthly(true)}
            />
            <Button
              rounded
              variant={isMonthly ? 'secondary' : 'primary'}
              text={t('yearly')}
              onClick={() => setIsMonthly(false)}
            />
            <CurrencySelector
              value={currency}
              isLocked={isCurrencyLocked}
              onChange={setPickedCurrency}
            />
            <Menu buttonProps={{ variant: 'secondary', iconRight: <IconMoreVertical /> }}>
              <Menu.Item
                label={t('cancelPlan')}
                variant="danger"
                icon={<IconTriggerOff />}
                onSelect={showMessenger}
              />
            </Menu>
          </Stack>
        </Stack>
        <TableContainer>
          <Row>
            <Cell></Cell>
            <Cell>
              <Text font="semibold14" text={t('business')} />
            </Cell>
            <Cell>
              <Text font="semibold14" text={t('premium')} />
            </Cell>
            <Cell>
              <Text font="semibold14" text={t('enterprise')} />
            </Cell>
          </Row>
          <Row>
            <Cell>
              <Text font="semibold14" text={t('creditsPerMonth')} />
            </Cell>
            <Cell>
              <Stack orientation="horizontal">
                <Text font="semibold14" text={formatNumber(3000)} />
                <Text font="regular14" text={t('month')} />
              </Stack>
            </Cell>
            <Cell>
              <Stack orientation="horizontal">
                <Text font="semibold14" text={formatNumber(18000)} />
                <Text font="regular14" text={t('month')} />
              </Stack>
            </Cell>
            <Cell>
              <Stack orientation="horizontal">
                <Text font="regular14" text={t('custom')} />
              </Stack>
            </Cell>
          </Row>
          <Row>
            <Cell>
              <Text font="semibold14" text={t('costMonthly')} />
            </Cell>
            <Cell>
              <Stack orientation="horizontal">
                {businessPrice === null ? (
                  <Text font="regular14" text={t('getAQuote')} />
                ) : (
                  <>
                    <Text font="semibold14" text={businessPrice} />
                    <Text font="regular14" text={t('month')} />
                  </>
                )}
              </Stack>
            </Cell>
            <Cell>
              <Stack orientation="horizontal">
                {premiumPrice === null ? (
                  <Text font="regular14" text={t('getAQuote')} />
                ) : (
                  <>
                    <Text font="semibold14" text={premiumPrice} />
                    <Text font="regular14" text={t('month')} />
                  </>
                )}
              </Stack>
            </Cell>
            <Cell>
              <Stack orientation="horizontal">
                <Text font="regular14" text={t('getAQuote')} />
              </Stack>
            </Cell>
          </Row>
          <Row $isLast>
            <Cell></Cell>
            <Cell>
              {downgradeToPlanType === 'business' ? (
                <CancelDowngradeButton />
              ) : chargebeePlanPageMutation.isLoading && planLoading === 'business' ? (
                <RelativeSpinner />
              ) : (
                <Button
                  isFullWidth
                  size="sm"
                  isDisabled={currentPlan === 'business'}
                  variant={getButtonVariant('business')}
                  text={getButtonText('business')}
                  onClick={() => handlePlanButtonClick('business')}
                />
              )}
            </Cell>
            <Cell>
              {downgradeToPlanType === 'premium' ? (
                <CancelDowngradeButton />
              ) : chargebeePlanPageMutation.isLoading && planLoading === 'premium' ? (
                <RelativeSpinner />
              ) : (
                <Button
                  isFullWidth
                  size="sm"
                  isDisabled={currentPlan === 'premium'}
                  variant={getButtonVariant('premium')}
                  text={getButtonText('premium')}
                  onClick={() => handlePlanButtonClick('premium')}
                />
              )}
            </Cell>
            <Cell>
              <Button
                isFullWidth
                size="sm"
                isDisabled={currentPlan === 'enterprise'}
                variant={getButtonVariant('enterprise')}
                text={getButtonText('enterprise')}
                onClick={showMessenger}
              />
            </Cell>
          </Row>
        </TableContainer>
      </Stack>
    </Card>
  );
};

const CancelDowngradeButton = () => {
  const { t } = useScopedTranslate(locale);
  const cancelMutation = useCancelDowngrade();

  return (
    <Button
      isFullWidth
      variant="primary-blue"
      size="sm"
      text={t('cancelDowngrade')}
      isDisabled={cancelMutation.isLoading}
      onClick={() => cancelMutation.mutate()}
    />
  );
};

const FeatureList = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Card space={6} variant="gray" hasBorder={false}>
      <Stack space={4}>
        <TableContainer>
          <Row>
            <Cell>
              <Text font="semibold14" text={t('features')} />
            </Cell>
            <Cell></Cell>
            <Cell></Cell>
            <Cell></Cell>
          </Row>
          {PLAN_FEATURES.map((item, index) => (
            <Row key={index}>
              <Cell>
                <Text font="semibold14" text={item.title} />
              </Cell>
              <Cell>
                <Text font="regular14" text={item.isBusiness ? '√' : ''} />
              </Cell>
              <Cell>
                <Text font="regular14" text={item.isPremium ? '√' : ''} />
              </Cell>
              <Cell>
                <Text font="regular14" text="√" />
              </Cell>
            </Row>
          ))}
        </TableContainer>
        <TableContainer>
          <Row>
            <Cell>
              <Text font="semibold14" text={t('support')} />
            </Cell>
            <Cell></Cell>
            <Cell></Cell>
            <Cell></Cell>
          </Row>
          {PLAN_SUPPORT.map((item, index) => (
            <Row key={index}>
              <Cell>
                <Text font="semibold14" text={item.title} />
              </Cell>
              <Cell>
                <Text font="regular14" text={item.isBusiness ? '√' : ''} />
              </Cell>
              <Cell>
                <Text font="regular14" text={item.isPremium ? '√' : ''} />
              </Cell>
              <Cell>
                <Text font="regular14" text="√" />
              </Cell>
            </Row>
          ))}
        </TableContainer>
        <TableContainer>
          <Row>
            <Cell>
              <Text font="semibold14" text={t('security')} />
            </Cell>
            <Cell></Cell>
            <Cell></Cell>
            <Cell></Cell>
          </Row>
          {PLAN_SECURITY.map((item, index) => (
            <Row key={index} $isLast>
              <Cell>
                <Text font="semibold14" text={item.title} />
              </Cell>
              <Cell>
                <Text font="regular14" text={item.isBusiness ? '√' : ''} />
              </Cell>
              <Cell>
                <Text font="regular14" text={item.isPremium ? '√' : ''} />
              </Cell>
              <Cell>
                <Text font="regular14" text="√" />
              </Cell>
            </Row>
          ))}
        </TableContainer>
      </Stack>
    </Card>
  );
};

const TableContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: '2fr repeat(3, 1fr)',
  width: '100%',
  borderCollapse: 'collapse',
});

const Row = styled.div<{ $isLast?: boolean }>((p) => ({
  display: 'contents',
  alignItems: 'center',
  '& > *': {
    borderBottom: p.$isLast ? 'none' : `${pxToRem(1)} solid ${theme.color.border10}`,
  },
}));

const Cell = styled.div({
  padding: pxToRem(8),
  alignContent: 'center',
  '> span': {
    lineHeight: pxToRem(32),
  },
});

const locale = {
  en: {
    plans: 'Plans',
    learnMore: 'Learn more',
    pickAPlan: 'Pick a plan',
    monthly: 'Monthly',
    yearly: 'Yearly • Save 20%',
    month: ' /month',
    creditsPerMonth: 'Credits per month',
    costMonthly: 'Cost monthly',
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    getAQuote: 'Get a quote',
    cancelPlan: 'Cancel plan',
    custom: 'Custom',
    business: 'Business',
    premium: 'Premium',
    enterprise: 'Enterprise',
    features: 'Features',
    customLists: 'Custom lists',
    syncedLists: 'Synced lists',
    basicActions: 'Basic actions',
    aiAgents: 'AI agents',
    aiJourneyHelper: 'AI journey helper',
    workspaces: 'Workspaces',
    privateWorkspaces: 'Private workspaces',
    apiAccessKey: 'API access key',
    documentTemplates: 'Document templates',
    bulkImport: 'Bulk import',
    customEmailDomain: 'Custom email domain',
    customPortalUrl: 'Custom portal URL',
    webhookActions: 'Webhook actions',
    codeAction: 'Code action',
    calendarEventAction: 'Calendar event action',
    customPaymentOptions: 'Custom payment options',
    annualCreditLimits: 'Annual credit limits',
    support: 'Support',
    liveSupport: 'Live support',
    retainer: 'Retainer with experts (limited availability)',
    dedicatedAccountManager: 'Dedicated account manager',
    dedicatedSupportEngineer: 'Dedicated support engineer',
    prioritySupport: 'Priority support',
    security: 'Security',
    azureSSO: 'Azure Single sign-on (SSO)',
    currentPlan: 'Current plan',
    unableToOpenPage: 'Unable to open checkout page, please try again later.',
    contactUs: 'Please contact us for plan changes',
    contactUsCurrency: 'Please contact us for currency changes',
    cancelDowngrade: 'Cancel downgrade',
  },
};

const { t } = getScopedTranslate(locale);

const PLAN_FEATURES = [
  {
    title: t('customLists'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('syncedLists'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('basicActions'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('aiAgents'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('aiJourneyHelper'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('workspaces'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('privateWorkspaces'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('apiAccessKey'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('documentTemplates'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('bulkImport'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('customEmailDomain'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('customPortalUrl'),
    isBusiness: false,
    isPremium: true,
  },
  {
    title: t('webhookActions'),
    isBusiness: false,
    isPremium: true,
  },
  {
    title: t('codeAction'),
    isBusiness: false,
    isPremium: true,
  },
  {
    title: t('calendarEventAction'),
    isBusiness: false,
    isPremium: true,
  },
  {
    title: t('customPaymentOptions'),
    isBusiness: false,
    isPremium: false,
  },
  {
    title: t('annualCreditLimits'),
    isBusiness: false,
    isPremium: false,
  },
];

const PLAN_SUPPORT = [
  {
    title: t('liveSupport'),
    isBusiness: true,
    isPremium: true,
  },
  {
    title: t('retainer'),
    isBusiness: false,
    isPremium: true,
  },
  {
    title: t('dedicatedAccountManager'),
    isBusiness: false,
    isPremium: false,
  },
  {
    title: t('dedicatedSupportEngineer'),
    isBusiness: false,
    isPremium: false,
  },
  {
    title: t('prioritySupport'),
    isBusiness: false,
    isPremium: false,
  },
];

const PLAN_SECURITY = [
  {
    title: t('azureSSO'),
    isBusiness: false,
    isPremium: false,
  },
];
